/**
 * SEO Component - Manages meta tags, Open Graph, and structured data
 * Uses react-helmet-async for dynamic meta tag management
 */

import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import {
  getPageMeta,
  SITE_URL,
  SITE_NAME,
  SITE_LOCALE,
} from "../seo/page-meta";
import {
  getOrganizationSchema,
  getWebApplicationSchema,
  getFAQSchema,
  getBlogPostSchema,
  getBreadcrumbSchema,
  getEnhancedLocalBusinessSchema,
  getQuantitySurveyServiceSchema,
  getFeasibilityStudyServiceSchema,
  getBuildingCodeConsultingSchema,
} from "../seo/structured-data";

interface SEOProps {
  /** Override page title (optional - uses route-based defaults) */
  title?: string;
  /** Override meta description (optional - uses route-based defaults) */
  description?: string;
  /** Override keywords (optional - uses route-based defaults) */
  keywords?: string[];
  /** Override canonical URL path (optional) */
  canonicalPath?: string;
  /** Open Graph type - website, article, etc. */
  ogType?: "website" | "article";
  /** Open Graph image URL */
  ogImage?: string;
  /** For article pages - publish date */
  articlePublishedTime?: string;
  /** For article pages - modified date */
  articleModifiedTime?: string;
  /** For article pages - author name */
  articleAuthor?: string;
  /** Custom structured data (JSON-LD) */
  structuredData?: object | object[];
  /** FAQs for FAQ schema */
  faqs?: Array<{ question: string; answer: string }>;
  /** Breadcrumb items */
  breadcrumbs?: Array<{ name: string; url: string }>;
  /** Prevent indexing (for dynamic/private pages) */
  noIndex?: boolean;
  /** Blog post data for article schema */
  blogPost?: {
    title: string;
    description: string;
    slug: string;
    datePublished: string;
    dateModified?: string;
    authorName: string;
    authorSlug: string;
    image?: string;
    keywords?: string[];
  };
}

export function SEO({
  title: titleOverride,
  description: descriptionOverride,
  keywords: keywordsOverride,
  canonicalPath: canonicalOverride,
  ogType: ogTypeOverride,
  ogImage,
  articlePublishedTime,
  articleModifiedTime,
  articleAuthor,
  structuredData,
  faqs,
  breadcrumbs,
  noIndex = false,
  blogPost,
}: SEOProps) {
  const location = useLocation();
  const pageMeta = getPageMeta(location.pathname);

  // Use overrides or defaults from page meta
  const title = titleOverride || pageMeta.title;
  const description = descriptionOverride || pageMeta.description;
  const keywords = keywordsOverride || pageMeta.keywords;
  const canonicalPath = canonicalOverride || pageMeta.canonicalPath;
  const ogType = ogTypeOverride || pageMeta.ogType || "website";
  const canonicalUrl = `${SITE_URL}${canonicalPath}`;
  const defaultOgImage = `${SITE_URL}/og-image.png`;

  // Build structured data array
  const structuredDataArray: object[] = [];

  // Always include organization schema
  structuredDataArray.push(getOrganizationSchema());

  // Include enhanced local business schema on landing page
  if (canonicalPath === "/") {
    structuredDataArray.push(getEnhancedLocalBusinessSchema());
  }

  // Add page-specific service schemas
  if (canonicalPath === "/quantity-survey") {
    structuredDataArray.push(getQuantitySurveyServiceSchema());
  }
  if (canonicalPath === "/feasibility") {
    structuredDataArray.push(getFeasibilityStudyServiceSchema());
  }
  if (canonicalPath === "/chat") {
    structuredDataArray.push(getBuildingCodeConsultingSchema());
  }

  // Add page-specific schemas
  if (pageMeta.structuredDataType === "WebApplication") {
    structuredDataArray.push(
      getWebApplicationSchema(
        title.split(" | ")[0], // Use first part of title as app name
        description,
        canonicalUrl,
      ),
    );
  }

  // Add FAQ schema if provided
  if (faqs && faqs.length > 0) {
    structuredDataArray.push(getFAQSchema(faqs));
  }

  // Add breadcrumb schema if provided
  if (breadcrumbs && breadcrumbs.length > 0) {
    structuredDataArray.push(getBreadcrumbSchema(breadcrumbs));
  }

  // Add blog post schema
  if (blogPost) {
    structuredDataArray.push(getBlogPostSchema(blogPost));
  }

  // Add custom structured data
  if (structuredData) {
    if (Array.isArray(structuredData)) {
      structuredDataArray.push(...structuredData);
    } else {
      structuredDataArray.push(structuredData);
    }
  }

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords.join(", ")} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Indexing Control */}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta
          name="robots"
          content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        />
      )}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage || defaultOgImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={SITE_LOCALE} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage || defaultOgImage} />

      {/* Article-specific meta (for blog posts) */}
      {ogType === "article" && articlePublishedTime && (
        <meta
          property="article:published_time"
          content={articlePublishedTime}
        />
      )}
      {ogType === "article" && articleModifiedTime && (
        <meta property="article:modified_time" content={articleModifiedTime} />
      )}
      {ogType === "article" && articleAuthor && (
        <meta property="article:author" content={articleAuthor} />
      )}

      {/* Geographic targeting for local SEO - Calgary, Alberta, Canada */}
      <meta name="geo.region" content="CA-AB" />
      <meta name="geo.placename" content="Calgary" />
      <meta name="geo.position" content="51.0447;-114.0719" />
      <meta name="ICBM" content="51.0447, -114.0719" />

      {/* Extended geo-targeting for search engines */}
      <meta name="DC.coverage" content="Calgary, Alberta, Canada" />
      <meta name="distribution" content="Canada" />
      <meta
        name="target"
        content="Calgary, Airdrie, Cochrane, Okotoks, Chestermere, Rocky View County, Alberta"
      />

      {/* Professional audience targeting */}
      <meta
        name="audience"
        content="Real estate developers, architects, structural engineers, building code consultants, contractors, quantity surveyors, homeowners"
      />

      {/* Content language and region */}
      <meta httpEquiv="content-language" content="en-CA" />

      {/* Alternate hreflang for regional targeting */}
      <link rel="alternate" hrefLang="en-CA" href={canonicalUrl} />
      <link rel="alternate" hrefLang="en" href={canonicalUrl} />
      <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />

      {/* Structured Data (JSON-LD) */}
      {structuredDataArray.map((data, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  );
}

export default SEO;
